import i18next from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type UserPreferences, usePreferences } from '../data/userProfile.ts'
import { getConversionFunction } from './measurements/convertMeasurements.tsx'

export type RainUnit = UserPreferences['unitsRain']

// Bounds keep 2 decimals rather than rounding to whole numbers like `useDistanceRangeHelpers` does: rain
// bounds in inches are small enough (1 mm ≈ 0.04 in)
const DISPLAY_DECIMALS = 2
const factor = 10 ** DISPLAY_DECIMALS

const buildRainRangeHelpers = (unit: RainUnit, min: number, max: number, formattedUnit: string) => {
	const identity = (value: number) => value
	const toUserPreference = getConversionFunction('PRECIPITATION_ACCUMULATION', unit, 'MILLIMETRES') ?? identity
	const toMillimetres = getConversionFunction('PRECIPITATION_ACCUMULATION', 'MILLIMETRES', unit) ?? identity

	const fromMmToUserPreference = (mm: number, decimals = DISPLAY_DECIMALS) => Number(toUserPreference(mm).toFixed(decimals))

	return {
		rainUnit: unit,
		fromMmToUserPreference,
		fromUserPreferenceToMm: (value: number, decimals = DISPLAY_DECIMALS) => Number(toMillimetres(value).toFixed(decimals)),
		formattedUnit,
		formatMmInUserPreferenceUnit: (mm: number, delimiter = ' ', decimals = DISPLAY_DECIMALS) =>
			`${fromMmToUserPreference(mm, decimals)}${delimiter}${formattedUnit}`,
		minRainInUserPreferenceUnit: Math.ceil(toUserPreference(min) * factor) / factor,
		maxRainInUserPreferenceUnit: Math.floor(toUserPreference(max) * factor) / factor,
	}
}

export const rainRangeHelpers = (unit: RainUnit, min = 0, max = 100) =>
	buildRainRangeHelpers(
		unit,
		min,
		max,
		i18next.t(($) => $.measurementUnitLabels[unit], { ns: 'measurementTypes' }),
	)

export const useRainRangeHelpers = (min = 0, max = 100) => {
	const { data: preferences } = usePreferences()
	const { t } = useTranslation()
	const rainUnit = preferences.unitsRain ?? 'MILLIMETRES'

	return useMemo(() => {
		const formattedUnit = t(($) => $.measurementUnitLabels[rainUnit], { ns: 'measurementTypes' })
		return buildRainRangeHelpers(rainUnit, min, max, formattedUnit)
	}, [rainUnit, min, max, t])
}
